<script setup lang="ts">
import { tpgIconArrowBack, tpgIconClose, tpgIconLayersFull } from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import {
  GrammarBadge,
  GrammarDivider,
  GrammarFABToggle,
  GrammarQuaternaryButton,
  GrammarTruncate,
  GrammarWorkflowPanel,
} from '@nora-monorepo/grammar-ui';
import { useWebViewStore } from '@nora-monorepo/ui-shared';

import OperationLayersView from '#nora-ui/map/components/overlay-menu/OperationLayersView.vue';
import OperationsListView from '#nora-ui/map/components/overlay-menu/OperationsListView.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';

const props = defineProps<{
  mapId: string;
}>();
const { mapId } = props;

const webViewStore = useWebViewStore();
const mapStore = useMapStore();
const operationStore = useOperationStore();

const map = mapStore.getMapByIdOrThrow(mapId);

const { isWebView } = storeToRefs(webViewStore);

/**
 * The operation whose layers the panel shows. Navigation state only, separate from the
 * map's active operation.
 */
const viewedOperationId = ref<string | undefined>(map.ref.activeOperationId);

const viewedOperation = computed(() => operationStore.getOperation(viewedOperationId.value));

/**
 * Whether the panel shows an operation's layers instead of the operations list.
 */
const insideOperation = computed((): boolean => viewedOperation.value !== undefined);

// Keep the panel in sync with the map's active operation, but only while it is already
// inside an operation's details: activating from the list (e.g. the eye toggle making the
// first shown operation the draw target) must not navigate into the operation.
watch(() => map.ref.activeOperationId, (activeOperationId) => {
  if (viewedOperationId.value !== undefined) {
    viewedOperationId.value = activeOperationId;
  }
});

const layerLabel = computed((): string => {
  if (map.state.draw.activeDrawLayerLabel.value) {
    return map.state.draw.activeDrawLayerLabel.value;
  }

  return 'Layer';
});

/**
 * The active operation's name, shown on the collapsed FAB badge.
 */
const operationLabel = computed((): string | undefined => {
  if (map.ref.activeOperation) {
    return map.ref.activeOperation.name;
  }

  return undefined;
});

/**
 * The viewed operation's name, shown in the panel header.
 */
const viewedOperationLabel = computed((): string | undefined => viewedOperation.value?.name);

function toggleMenu() {
  map.state.draw.toggleDrawLayerMenuOpen();
}

function onClose() {
  map.state.draw.setDrawLayerMenuOpen(false);
}

async function selectOperation(operationId: string, showDetails: boolean) {
  if (!map.ref.isOperationVisible(operationId)) {
    await map.showOperation(operationId);
  }

  map.setActiveOperation(operationId);
  if (showDetails) {
    viewedOperationId.value = operationId;
  }
}

function backToOperations() {
  viewedOperationId.value = undefined;
}
</script>

<template>
  <div
    v-if="isWebView && !map.state.draw.drawLayerMenuOpen"
    class="absolute z-30 pointer-events-auto left-5 top-4 max-w-2/3"
  >
    <GrammarFABToggle @click="toggleMenu">
      <template #badge>
        <GrammarBadge
          :top-label="operationLabel"
          :label="layerLabel"
          emphasis="no-background"
          :trailing-icon-template="tpgIconLayersFull"
        />
      </template>
    </GrammarFABToggle>
  </div>
  <div
    v-if="map.state.draw.drawLayerMenuOpen"
    class="absolute z-50 top-11 bottom-0 pointer-events-auto rounded-sm overflow-hidden"
    :class="{
      'max-w-full right-0': !isWebView,
      'w-full': isWebView,
    }"
  >
    <GrammarWorkflowPanel class="max-w-full h-full flex flex-col md:w-90">
      <template #panel-content>
        <div class="h-full bg-light-bg-primary dark:bg-dark-bg-primary flex flex-col">
          <div class="sticky -top-2 z-10 bg-light-bg-primary dark:bg-dark-bg-primary text-light-label-tertiary dark:text-dark-label-tertiary py-2 flex items-center justify-between">
            <template v-if="insideOperation">
              <GrammarQuaternaryButton
                :icon-template="tpgIconArrowBack"
                size="small"
                label="Operations"
                @click="backToOperations"
              />

              <span class="px-4 inline-block w-full text-left max-w-50">
                <GrammarTruncate
                  :label="viewedOperationLabel ?? 'Operation'"
                  :truncate-end="10"
                />
              </span>

              <GrammarQuaternaryButton
                size="small"
                :icon-template="tpgIconClose"
                @click="onClose"
              />
            </template>
            <template v-else>
              <span class="font-semibold absolute left-1/2 -translate-x-1/2">
                Operations
              </span>

              <GrammarQuaternaryButton
                size="small"
                class="ml-auto"
                :icon-template="tpgIconClose"
                @click="onClose"
              />
            </template>
          </div>

          <GrammarDivider orientation="horizontal" />
          <OperationLayersView
            v-if="viewedOperation"
            :map-id="mapId"
            :operation-id="viewedOperation.id"
          />
          <OperationsListView
            v-else
            :map-id="mapId"
            @select-operation="selectOperation"
          />
        </div>
      </template>
    </GrammarWorkflowPanel>
  </div>
</template>
